import {
  Box,
  Button,
  CircularProgress,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AdminPageLoader from 'csam/admin/components/AdminPageLoader';
import AdminWrapper from 'csam/admin/components/AdminWrapper';
import LocaleButtonGroup from 'csam/admin/components/LocaleButtonGroup';
import useTranslatableFields from 'csam/admin/hooks/UseTranslateble,';
import AdminInternalError from 'csam/admin/pages/AdminInternalError';
import { useAuthenticatedMutation, useAuthenticatedQuery } from 'csam/api/api';
import LocaleContext from 'csam/components/LocaleContext';
import { fixed, getImage } from 'csam/utils/Constants';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

interface OctContestGuidelinesData {
  year_id: number;
  contest_id: number;
  image: string | File;
  status: number;
  [key: string]: string | number | File;
}

interface TranslationData {
  image: string | File;
  [key: string]: unknown;
}

interface State {
  data: OctContestGuidelinesData;
  translations: {
    es: TranslationData;
    zh: TranslationData;
    pt: TranslationData;
  };
}

const AddOctContestsGuidelines: React.FC = () => {
  const initialState = {
    data: {
      year_id: 0,
      contest_id: 0,
      image: '',
      status: 0,
    },
    translations: {
      es: { image: '' },
      pt: { image: '' },
      zh: { image: '' },
    },
  };

  const { locale } = useContext(LocaleContext);
  const [octContestGuidelineData, setOctContestGuidelineData] = useState<State>(initialState);
  const [loading, setLoading] = useState(false);
  const imageName = octContestGuidelineData.data.image;

  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();

  const slug = `oct_contests_guidelines`;

  const { translatePending, errorTranslate, translateData, disabledFields, setLocale } = useTranslatableFields(
    slug,
    initialState,
  );

  useEffect(() => {
    if (!id) {
      setLocale('en');
    }
  }, [id, setLocale]);

  const query = useAuthenticatedQuery(
    ['OctContestGuidelines', id],
    {
      url: `${fixed}admin/en/oct_contests_guidelines/${id}`,
    },
    {
      enabled: !!id,
    },
  );

  useEffect(() => {
    if (query.data && query.data.success) {
      setOctContestGuidelineData(query.data.data as State);
    }
  }, [query.data]);

  const isUpdateForm = Boolean(id);

  const mutation = useAuthenticatedMutation(
    isUpdateForm ? ['update-oct-contest-Guidelines', 'PUT'] : ['add-oct-contest-Guidelines', 'POST'],

    (data) => {
      const headers = data instanceof FormData ? {} : { 'Content-Type': 'application/json' };

      return {
        url: `${fixed}admin/${locale}/oct_contests_guidelines${isUpdateForm ? `/${id}` : ''}`,

        method: isUpdateForm ? 'PUT' : 'POST',

        headers,

        data,
      };
    },
  );

  // const mutation = useAuthenticatedMutation(
  //   isUpdateForm ? ['update-oct-contest-Guidelines', 'PUT'] : ['add-oct-contest-Guidelines', 'POST'],
  //   (data) => ({
  //     url: `${fixed}admin/${locale}/oct_contests_guidelines${isUpdateForm ? `/${id}` : ''}`,
  //     method: isUpdateForm ? 'PUT' : 'POST',
  //     headers: { 'Content-Type': 'application/json' },
  //     data,
  //   }),
  // );

  const {
    data: contestData,
    error: contestError,
    isPending: contestPending,
  } = useAuthenticatedQuery(['fetch-monts', 'GET'], {
    url: `${fixed}admin/en/oct_contests`,
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  
  const {
    data: yearsData,
    error: yearsError,
    isPending: yearsPending,
  } = useAuthenticatedQuery(['fetch-years', 'GET'], {
    url: `${fixed}admin/en/calender_years`,
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  interface YearData {
    id: number;
    year_id: string;
    year: string;
  }

  interface ContestData {
    id: number;
    contest_id: string;
    contest_title: string;
  }


  if (contestPending) return <AdminPageLoader />;
  if (contestError) return <div>Error: {contestError.message}</div>;
  if (contestData && !contestData.success) return <AdminInternalError />;
  
  if (yearsPending) return <AdminPageLoader />;
  if (yearsError) return <div>Error: {yearsError.message}</div>;
  if (yearsData && !yearsData.success) return <AdminInternalError />;

  if (translatePending) return <AdminPageLoader />;
  if (errorTranslate) return <div>Error: {errorTranslate.message}</div>;
  if (translateData && !translateData.success) return <AdminInternalError />;

  const handleChange = (event: { target: { name: string; value: string | number; checked?: boolean } }) => {
    // eslint-disable-next-line prefer-const
    let { name, value, checked } = event.target;

    if (name === 'status') {
      value = checked ? 1 : 0;
    }

    if (translateData) {
      const translatableFields = translateData.data;

      setOctContestGuidelineData((prevState) => {
        let newData = prevState.data;
        let newTranslations = prevState.translations;

        if (locale !== 'en' && translatableFields.includes(name)) {
          newTranslations = {
            ...prevState.translations,
            [locale as keyof typeof prevState.translations]: {
              ...(prevState.translations[locale as keyof typeof prevState.translations] || {}),
              [name]: value,
            },
          };
        } else {
          newData = {
            ...prevState.data,
            [name]: value,
          };
        }

        return {
          data: newData,
          translations: newTranslations,
        };
      });
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const date = new Date().toISOString();
      const tableName = 'oct_contests_guidelines';
      let fileName;

      if (locale === 'en') {
        fileName = `${date}_${tableName}_${file.name}`;
      } else {
        fileName = `${locale}_${tableName}_${file.name}`;
      }

      const newFile = new File([file], fileName, { type: file.type });

      setOctContestGuidelineData((prevData) => {
        let newData = { ...prevData.data };
        let newTranslations = { ...prevData.translations };
        if (locale === 'en') {
          newData = {
            ...newData,
            [event.target.name]: newFile,
          };
        } else {
          newTranslations = {
            ...newTranslations,
            [locale]: {
              ...(newTranslations[locale as keyof State['translations']] || {}),
              [event.target.name]: newFile,
            },
          };
        }
        return {
          data: newData,
          translations: newTranslations,
        };
      });
    }
  };

  const appendToFormData = (formData: FormData, data: Record<string, unknown>, baseKey: string) => {
    Object.entries(data).forEach(([key, value]) => {
      const fullKey = `${baseKey}[${key}]`;

      if (typeof value === 'object' && value !== null) {
        formData.append(fullKey, JSON.stringify(value));
      } else {
        formData.append(fullKey, String(value));
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setLoading(true);

    const formData = new FormData();

    const data = { ...octContestGuidelineData };

    if (data) {
      delete data.translations;
    }

    appendToFormData(formData, data, 'data');

    if (octContestGuidelineData.data.image instanceof File) {
      formData.append('image', octContestGuidelineData.data.image);
    }

    if (octContestGuidelineData?.translations) {
      Object.entries(octContestGuidelineData.translations).forEach(([localeKey, translation]) => {
        appendToFormData(formData, translation, `translations[${localeKey}]`);
      });
    }

    try {
      await mutation.mutateAsync(formData, {
        onSuccess: () => {
          toast.success(`Record ${isUpdateForm ? 'Updated' : 'Added'} Successfully`);
          setTimeout(() => {
            navigate('/admin/october-contest-guidelines');
          }, 1000);
        },

        onError: () => {
          console.log('An error occurred while saving the data.');
        },
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetLocale = (newLocale: string) => {
    setLocale(newLocale);
  };

  return (
    <AdminWrapper>
      <Box className="pageHeader">
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Typography variant="h6">
            {' '}
            {isUpdateForm ? 'Edit October Contest Guideline' : 'Add October contest Guideline'}{' '}
          </Typography>
        </Box>
        <LocaleButtonGroup currentLocale={locale} setLocale={handleSetLocale} />
      </Box>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item sm={12}>
            <InputLabel id="year-label">Year</InputLabel>
            <Select
              fullWidth
              labelId="year-label"
              name="year_id"
              size="small"
              value={octContestGuidelineData.data.year_id === 0 ? '' : octContestGuidelineData.data.year_id.toString()}
              onChange={handleChange}
              disabled={disabledFields.includes('year_id')}
              required={locale === 'en'}
            >
              {(yearsData.data as YearData[]).map((year: YearData) => (
                <MenuItem key={year.id} value={year.id.toString()}>
                  {year.year}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item sm={12}>
            <InputLabel id="year-label">Select Contest</InputLabel>
            <Select
              fullWidth
              labelId="year-label"
              name="contest_id"
              size="small"
              value={octContestGuidelineData.data.contest_id === 0 ? '' : octContestGuidelineData.data.contest_id.toString()}
              onChange={handleChange}
              disabled={disabledFields.includes('contest_id')}
              required={locale === 'en'}
            >
              {(contestData.data as ContestData[]).map((contest: ContestData) => (
                <MenuItem key={contest.id} value={contest.id.toString()}>
                  {contest.contest_title}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item sm={12}>
            <Box className="form-group">
              <InputLabel>
                Document <Typography component="sup">{locale.toUpperCase()}</Typography>
              </InputLabel>
              <TextField
                type="file"
                name="image"
                onChange={handleImageChange}
                disabled={disabledFields.includes('image')}
                required={locale === 'en' && !imageName}
                fullWidth
              />
              {imageName && (
                <iframe
                  className="formImage"
                  src={`${getImage}oct_contests_guidelines/${locale === 'en' ? imageName : octContestGuidelineData.translations[locale as keyof typeof octContestGuidelineData.translations]?.image}`}
                  width="100%"
                  height="500px"
                  title="PDF Preview"
                />
              )}
            </Box>
          </Grid>

          <Grid item sm={6}>
            <InputLabel id="status-label">Status</InputLabel>
            <Grid container alignItems="center">
              <Typography>Inactive</Typography>
              <Switch
                id="status-label"
                name="status"
                onChange={handleChange}
                checked={Boolean(octContestGuidelineData.data.status)}
                disabled={locale !== 'en'}
              />
              <Typography>Active</Typography>
            </Grid>
          </Grid>

          <Grid item sm={12}>
            <Button type="submit" variant="contained" color="primary" disabled={loading}>
              {loading && <CircularProgress size={16} sx={{ mr: 1 }} />} Submit
            </Button>
          </Grid>
        </Grid>
      </Box>
    </AdminWrapper>
  );
};

export default AddOctContestsGuidelines;
